import React, { CSSProperties, FunctionComponent, ReactNode } from "react";
import "./DataList.scss";

export interface DataListColumn<T> {
    /** Chave unica da coluna */
    key: string;
    /** Cabecalho */
    header: ReactNode;
    /** Como renderizar a celula */
    render: (row: T, index: number) => ReactNode;
    /** Largura customizada (grid fraction). Default 1fr */
    width?: string;
    /** Alinhamento horizontal */
    align?: "left" | "center" | "right";
    /** Se true, aplica fonte mono (numbers, hashes) */
    mono?: boolean;
}

interface DataListProps<T> {
    columns: DataListColumn<T>[];
    rows: T[];
    /** Mensagem ou nó quando rows.length === 0 */
    empty?: ReactNode;
    /** Mostrar skeleton enquanto rows === undefined/null */
    loading?: boolean;
    /** chave da linha (default: index) */
    rowKey?: (row: T, index: number) => string | number;
    /** click em uma linha inteira */
    onRowClick?: (row: T, index: number) => void;
    className?: string;
}

const buildGridTemplate = <T,>(cols: DataListColumn<T>[]): string =>
    cols.map((c) => c.width ?? "1fr").join(" ");

/**
 * Lista de dados com header + rows estilizada (vidro, hover ciano, mono).
 * Substitui o padrao .header + .deposit-list/.list etc. duplicado em ~8 listagens.
 */
function DataList<T>({
    columns,
    rows,
    empty = "Nenhum item encontrado.",
    loading = false,
    rowKey,
    onRowClick,
    className = "",
}: DataListProps<T>) {
    const gridTemplate: CSSProperties = {
        gridTemplateColumns: buildGridTemplate(columns),
    };

    return (
        <div className={`data-list ${className}`.trim()}>
            <div className="dl-header" style={gridTemplate}>
                {columns.map((c) => (
                    <span key={c.key} className={`dl-h dl-align-${c.align ?? "left"} ${c.header}`}>
                        {c.header}
                    </span>
                ))}
            </div>

            <div className="dl-rows">
                {loading && (
                    <>
                        <div className="dl-skeleton" />
                        <div className="dl-skeleton" />
                        <div className="dl-skeleton" />
                    </>
                )}

                {!loading && rows.length === 0 && (
                    <div className="dl-empty">{empty}</div>
                )}

                {!loading && rows.map((row, i) => (
                    <div
                        key={rowKey ? rowKey(row, i) : i}
                        className={`dl-row ${onRowClick ? "clickable" : ""}`}
                        style={gridTemplate}
                        onClick={onRowClick ? () => onRowClick(row, i) : undefined}
                        role={onRowClick ? "button" : undefined}
                        tabIndex={onRowClick ? 0 : undefined}
                    >
                        {columns.map((c) => (
                            <span
                                key={c.key}
                                // No mobile a linha vira cartao e o cabecalho some, entao
                                // cada celula precisa carregar o proprio rotulo (o CSS o
                                // exibe via `content: attr(data-label)`).
                                data-label={typeof c.header === "string" ? c.header : undefined}
                                className={`dl-c dl-align-${c.align ?? "left"} ${c.mono ? "num" : ""}`.trim()}
                            >
                                {c.render(row, i)}
                            </span>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
}

// Helper para tipar a chamada com generico
const DataListTyped: <T>(p: DataListProps<T>) => React.ReactElement = DataList;

export default DataListTyped as FunctionComponent<DataListProps<any>>;
